/**
 * Go Damn — animates the cart drawer's coupon <details> open/close.
 *
 * Native <details> snaps open/closed instantly. This wraps it, intercepts the
 * summary click, and animates the panel height with the Web Animations API,
 * falling back to the native (instant) behavior with JS disabled or under
 * prefers-reduced-motion.
 */
class GdAccordion extends HTMLElement {
  /** @type {Animation | null} */
  #animation = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#animation?.cancel();
  }

  get #details() {
    return this.querySelector('details');
  }

  get #panel() {
    return this.querySelector('.gd-coupons__panel');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const summary = target.closest('summary');
    if (!summary || !this.contains(summary)) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const details = this.#details;
    const panel = this.#panel;
    if (!details || !panel) return;

    event.preventDefault();

    if (details.hasAttribute('open')) {
      this.#collapse(details, panel);
    } else {
      this.#expand(details, panel);
    }
  };

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   */
  #expand(details, panel) {
    details.setAttribute('open', '');
    // Let layout settle before measuring the panel's natural height.
    requestAnimationFrame(() => {
      this.#animate(panel, 0, panel.scrollHeight, () => {});
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   */
  #collapse(details, panel) {
    this.#animate(panel, panel.scrollHeight, 0, () => details.removeAttribute('open'));
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} from
   * @param {number} to
   * @param {() => void} onFinish
   */
  #animate(panel, from, to, onFinish) {
    this.#animation?.cancel();
    panel.style.overflow = 'hidden';

    this.#animation = panel.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );

    this.#animation.onfinish = () => {
      panel.style.overflow = '';
      panel.style.height = '';
      onFinish();
    };
  }
}

if (!customElements.get('gd-accordion')) {
  customElements.define('gd-accordion', GdAccordion);
}
